import { createSlice } from "@reduxjs/toolkit";
import sum from "lodash/sum";
import uniqBy from "lodash/uniqBy";
// utils
import axios from "../../utils/axios";
//
import { dispatch } from "../store";

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  service: {},
  checkout: {
    errorMessage: "",
    quantity: 0,
    activeStep: 0,
    fixedPrice: {},
    description: "",
    address: {},
    dateTime: {
      time: {},
      date: {
        id: 0,
        day: 0,
        month: 0,
        year: 0,
        name: "",
      },
    },
    cart: [],
    total: 0,
    discount: 0,
    paymentMethod: "",
  },
};

const slice = createSlice({
  name: "service",
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getServiceSuccess(state, action) {
      state.isLoading = false;
      state.service = action.payload;
    },

    // CHECKOUT
    getCart(state, action) {
      const cart = action.payload;

      const subtotal = sum(
        cart.map((cartItem) => cartItem.price * cartItem.quantity)
      );
      const discount = cart.length === 0 ? 0 : state.checkout.discount;
      const shipping = cart.length === 0 ? 0 : state.checkout.shipping;
      const billing = cart.length === 0 ? null : state.checkout.billing;

      state.checkout.cart = cart;
      state.checkout.discount = discount;
      state.checkout.shipping = shipping;
      state.checkout.billing = billing;
      state.checkout.subtotal = subtotal;
      state.checkout.total = subtotal - discount;
    },

    addCart(state, action) {
      const service = action.payload;
      const isEmptyCart = state.checkout.cart.length === 0;

      if (isEmptyCart) {
        state.checkout.cart = [...state.checkout.cart, service];
      } else {
        state.checkout.cart = state.checkout.cart.map((_service) => {
          const isExisted = _service.id === service.id;
          if (isExisted) {
            return {
              ..._service,
              quantity: _service.quantity + 1,
            };
          }
          return _service;
        });
      }
      state.checkout.cart = uniqBy([...state.checkout.cart, service], "id");
    },

    deleteCart(state, action) {
      const updateCart = state.checkout.cart.filter(
        (item) => item.id !== action.payload
      );

      state.checkout.cart = updateCart;
    },

    resetCart(state) {
      state.checkout.activeStep = 0;
      state.checkout.cart = [];
      state.checkout.total = 0;
      state.checkout.subtotal = 0;
      state.checkout.discount = 0;
      state.checkout.shipping = 0;
      state.checkout.billing = null;
    },
    refreshSteps(state) {
      state.checkout.activeStep = 0;
    },

    onBackStep(state) {
      state.checkout.activeStep -= 1;
    },

    onNextStep(state) {
      state.checkout.activeStep += 1;
    },

    onGotoStep(state, action) {
      const goToStep = action.payload;
      state.checkout.activeStep = goToStep;
    },
    setFixedPrice(state, action) {
      const fixedPrice = action.payload;
      state.checkout.fixedPrice = fixedPrice;
    },
    setQuantity(state, action) {
      const quantity = action.payload;
      state.checkout.quantity = quantity;
    },
    increaseQuantity(state, action) {
      state.checkout.quantity++;
    },

    decreaseQuantity(state, action) {
      state.checkout.quantity--;
    },
    setTotal(state, action) {
      const total = action.payload;
      state.checkout.total = total;
    },
    setDescription(state, action) {
      const description = action.payload;
      state.checkout.description = description;
    },
    setTime(state, action) {
      const time = action.payload;
      state.checkout.dateTime.time = time;
    },
    setDate(state, action) {
      const date = action.payload;
      state.checkout.dateTime.date = date;
    },
    setAddress(state, action) {
      const address = action.payload;
      state.checkout.address = address;
    },

    setError(state, action) {
      const error = action.payload;
      state.checkout.errorMessage = error;
    },
    createBilling(state, action) {
      state.checkout.billing = action.payload;
    },

    applyDiscount(state, action) {
      const discount = action.payload;
      state.checkout.discount = discount;
      state.checkout.total = state.checkout.subtotal - discount;
    },
    setPaymentMethod(state, action) {
      const paymentMethod = action.payload;
      state.checkout.paymentMethod = paymentMethod;
    },
    finishOrder(state, action) {
      state.checkout = initialState.checkout;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  getCart,
  addCart,
  resetCart,
  onGotoStep,
  onBackStep,
  refreshSteps,
  onNextStep,
  deleteCart,
  createBilling,
  applyShipping,
  applyDiscount,
  setTotal,
  setQuantity,
  setDescription,
  increaseQuantity,
  decreaseQuantity,
  setDate,
  setTime,
  setAddress,
  setError,
  setFixedPrice,
  setPaymentMethod,
  finishOrder,
} = slice.actions;

// ----------------------------------------------------------------------
// ----------------------------------------------------------------------

export function getService(name) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/service/${name}`);
      dispatch(slice.actions.getServiceSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
